/* eslint-disable @typescript-eslint/no-use-before-define */
import { AppNavigationProvider } from '@euler/app/components/AppNavigationProvider';
import { useStatusBarStyle } from '@euler/app/components/AppStatusBar';
import { BackIcon } from '@euler/app/components/icons/BackIcon';
import { PlansScreen } from '@euler/app/flows/plans/pages/list';
import { Icon } from '@euler/components/Icomoon';
import { MultilineHeaderTitle } from '@euler/components/MultilineHeaderTitle';
import { ErrorFallback } from '@euler/components/error';
import { LayoutProviderView } from '@euler/components/layout/LayoutProvider';
import { FontFamily } from '@euler/components/typography';
import { PredefinedHitSlops } from '@euler/functions/predefinedHitSlops';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import {
  useIsDesktopLayout,
  useIsMobileLayout,
} from '@euler/functions/responsiveness';
import { ScreenToParams, spreadScreens } from '@euler/functions/spreadScreens';
import {
  WrappedNavigatorScreenContext,
  wrapNavigatorScreen,
} from '@euler/functions/wrapNavigatorScreen';
import { ScopedObservableSuspenseResourceContextProvider } from '@euler/utils/hooks';
import { BottomSheetModalProvider } from '@gorhom/bottom-sheet';
import { DrawerActions, useNavigation } from '@react-navigation/native';
import {
  TransitionPresets,
  createStackNavigator,
} from '@react-navigation/stack';
import * as Sentry from '@sentry/react';
import { LinearGradient } from 'expo-linear-gradient';
import { memo, useCallback, useMemo } from 'react';
import {
  StyleProp,
  StyleSheet,
  TouchableOpacity,
  ViewStyle,
  useWindowDimensions,
} from 'react-native';
import {
  SafeAreaInsetsContext,
  useSafeAreaInsets,
} from 'react-native-safe-area-context';

const Stack = createStackNavigator();

const Screens = {
  _main: PlansScreen,
};

export type MaintenancePlanNavParams = ScreenToParams<typeof Screens>;

type Props = {
  initialScreen?: keyof MaintenancePlanNavParams;
};

export const MaintenancePlanFlow = wrapNavigatorScreen(
  (props: Props) => {
    useStatusBarStyle('dark');
    return <Content {...props} />;
  },
  {
    title: '维保计划',
    headerShown: false,
  },
);

const Content = memo((props: Props) => {
  const isMobile = useIsMobileLayout();
  const isDesktop = useIsDesktopLayout();
  const { width } = useWindowDimensions();
  const { rpx } = useResponsiveContext();
  // This flow is rendered inside the bottom-tab container, which already owns
  // the bottom safe area (the tab bar). The gorhom bottom sheet still reads the
  // real bottom inset, double-counting it and floating its footer too high — so
  // zero the bottom inset for everything under the sheet provider.
  const insets = useSafeAreaInsets();
  const sheetInsets = useMemo(() => ({ ...insets, bottom: 0 }), [insets]);
  const contentContainerStyle = useMemo<StyleProp<ViewStyle> | undefined>(
    () =>
      isMobile
        ? undefined
        : {
            width: Math.min(width, 730),
            marginLeft: 'auto',
            marginRight: 'auto',
          },
    [isMobile, width],
  );
  return (
    <AppNavigationProvider>
      <WrappedNavigatorScreenContext.Provider value={{ contentContainerStyle }}>
        <LayoutProviderView fallbackToChildren style={{ flex: 1 }}>
          <Sentry.ErrorBoundary
            // @ts-ignore
            fallback={(params: any) => <ErrorFallback {...params} />}
          >
            <ScopedObservableSuspenseResourceContextProvider
              scope={'maintenance-plan'}
            >
              <SafeAreaInsetsContext.Provider value={sheetInsets}>
                <BottomSheetModalProvider>
                  <Stack.Navigator
                    initialRouteName={props.initialScreen ?? '_main'}
                    screenOptions={{
                      ...TransitionPresets.SlideFromRightIOS,
                      headerMode: 'float',
                      headerTitleAlign: isDesktop ? 'left' : 'center',
                      headerBackTitle: ' ',
                      headerTitleStyle: {
                        fontSize: rpx(isDesktop ? 20 : 18),
                        fontFamily: FontFamily.NotoSans.Bold,
                        color: isDesktop ? '#0f172a' : '#fff',
                      },
                      headerStyle: isDesktop
                        ? {
                            backgroundColor: '#fff',
                            borderBottomWidth: 1,
                            borderBottomColor: '#e5e7eb',
                            shadowOpacity: 0,
                            elevation: 0,
                          }
                        : undefined,
                      cardStyle: {
                        backgroundColor: '#eee',
                      },
                      headerTitle: MultilineHeaderTitle,
                      headerLeft: () => <PlansHeaderLeft />,
                      headerBackImage: () => (
                        <BackIcon fill={isDesktop ? '#0f172a' : '#fff'} />
                      ),
                      headerBackground: isDesktop ? undefined : HeaderBg,
                    }}
                  >
                    {spreadScreens(
                      Stack.Screen,
                      Screens,
                      props.initialScreen ?? '_profile',
                      {},
                    )}
                  </Stack.Navigator>
                </BottomSheetModalProvider>
              </SafeAreaInsetsContext.Provider>
            </ScopedObservableSuspenseResourceContextProvider>
          </Sentry.ErrorBoundary>
        </LayoutProviderView>
      </WrappedNavigatorScreenContext.Provider>
    </AppNavigationProvider>
  );
});

/** Hamburger that opens the app drawer — only this tab's root screen shows it
 * (the flow has no sub-screens, so it never replaces a back button). */
const PlansHeaderLeft = memo(() => {
  const { rpx } = useResponsiveContext();
  const isDesktop = useIsDesktopLayout();
  const navigation = useNavigation();
  const onPress = useCallback(
    () => navigation.dispatch(DrawerActions.openDrawer()),
    [navigation],
  );
  if (isDesktop) return null;
  return (
    <TouchableOpacity
      onPress={onPress}
      hitSlop={PredefinedHitSlops.large}
      style={{ paddingLeft: rpx(16), paddingRight: rpx(12) }}
    >
      <Icon name="bars" color="#fff" size={rpx(20)} />
    </TouchableOpacity>
  );
});

const HeaderBg = () => (
  <LinearGradient
    //colors={['#3aa17d', '#00AA55']}
    colors={['#009FD4', '#2669db']}
    start={{ x: 0, y: 0 }}
    end={{ x: 1, y: 0 }}
    locations={[0.57, 1]}
    style={StyleSheet.absoluteFill}
  />
);
